import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  IndianRupee, Plus, Edit3, Trash2, Search, FileUp, Download,
  FileText, Image as ImageIcon, Loader2, Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';

const ALLOWED_EXTS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png'];
const MAX_MB = 10;

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function Required() {
  return <span className="text-red-400 ml-0.5" aria-hidden="true">*</span>;
}

export default function RateCardsPage() {
  const { isOwner, hasPerm } = useAuth();
  // Owner + Admin are covered by `isOwner` in the auth context (see AuthContext.js).
  const canEdit = isOwner;
  const canSee = hasPerm('rate_cards') || isOwner;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  // Create / edit dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: '', service: '', unit: '', price: 0, description: '', notes: '' });
  const [saving, setSaving] = useState(false);
  // Per-row upload state so multiple rows don't share a spinner
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputsRef = useRef({});

  const loadRates = async () => {
    if (!canSee) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/rate-cards');
      setItems(res.data);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadRates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term
      ? items.filter((r) => `${r.category} ${r.service} ${r.description || ''} ${r.notes || ''}`.toLowerCase().includes(term))
      : items;
    return list.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {});
  }, [items, q]);

  const openCreate = () => {
    setEditing(null);
    setForm({ category: '', service: '', unit: '', price: 0, description: '', notes: '' });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      category: r.category || '', service: r.service || '',
      unit: r.unit || '', price: r.price || 0,
      description: r.description || '', notes: r.notes || '',
    });
    setOpen(true);
  };

  const canSubmit = form.category.trim() && form.service.trim();

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        category: form.category.trim(),
        service: form.service.trim(),
        unit: form.unit || '',
        price: parseFloat(form.price) || 0,
        description: form.description || '',
        notes: form.notes || '',
      };
      if (editing) await api.patch(`/rate-cards/${editing.id}`, payload);
      else await api.post('/rate-cards', payload);
      setOpen(false);
      toast.success(editing ? 'Rate card updated.' : 'Rate card created.');
      loadRates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save rate card');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!confirm(`Delete rate card "${r.service}"? Any attached files will also be removed.`)) return;
    try {
      await api.delete(`/rate-cards/${r.id}`);
      toast.success('Rate card deleted.');
      loadRates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  // -------- Attachment handlers --------
  const openFilePicker = (rcId) => {
    fileInputsRef.current[rcId]?.click();
  };

  const handleAttachmentPick = async (rcId, e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';  // allow re-picking the same file later
    if (!files.length) return;

    // Client-side validation up-front for snappy feedback.
    for (const f of files) {
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      const mime = (f.type || '').toLowerCase();
      if (!ALLOWED_EXTS.includes(ext) && !ALLOWED_MIMES.includes(mime)) {
        toast.error(`"${f.name}" — only PDF, JPG, JPEG, PNG allowed.`);
        return;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`"${f.name}" is ${(f.size / (1024 * 1024)).toFixed(1)} MB — max ${MAX_MB} MB.`);
        return;
      }
    }

    setUploadingFor(rcId);
    try {
      // Upload sequentially to keep the debug/log output ordered & memory low.
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/rate-cards/${rcId}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(files.length === 1 ? 'File attached.' : `${files.length} files attached.`);
      loadRates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFor(null);
    }
  };

  const downloadAttachment = async (rc, att) => {
    try {
      const res = await api.get(`/rate-cards/${rc.id}/attachments/${att.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: att.mime || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    }
  };

  const removeAttachment = async (rc, att) => {
    if (!confirm(`Remove attached file "${att.filename}"?`)) return;
    try {
      await api.delete(`/rate-cards/${rc.id}/attachments/${att.id}`);
      toast.success('Attachment removed.');
      loadRates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove attachment');
    }
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader icon={IndianRupee} title="Rate Cards" subtitle="Access restricted." />
        <Card className="shine-card p-6 rounded-xl text-center text-slate-400">
          You don&apos;t have permission to view the rate card catalogue. Please contact Owner / Admin.
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={IndianRupee}
        title="Rate Cards"
        subtitle="Every service has its own rate card with description, images and PDFs."
        actions={canEdit && (
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="new-rc-btn">
            <Plus className="h-4 w-4 mr-1" /> New rate card
          </Button>
        )}
      />

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/70" />
        <Input
          placeholder="Search category, service or description…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 bg-[#0f2240] border-amber-500/20 text-white"
          data-testid="rc-search"
        />
      </div>

      {loading ? (
        <Card className="shine-card p-10 text-center rounded-xl">
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-amber-300" />
        </Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          {items.length === 0 ? 'No rate cards yet. Click “New rate card” to create one.' : 'No matches for your search.'}
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.keys(grouped).sort().map((cat) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="border-amber-500/40 text-amber-200 px-2 py-0.5">{cat}</Badge>
                <div className="flex-1 shine-divider" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {grouped[cat].map((r) => {
                  const uploading = uploadingFor === r.id;
                  return (
                    <Card key={r.id} className="shine-card p-4 rounded-xl" data-testid={`rc-card-${r.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h4 className="font-bold text-white break-words">{r.service}</h4>
                          {r.description && (
                            <p className="text-xs text-slate-300 mt-1 leading-relaxed whitespace-pre-wrap">{r.description}</p>
                          )}
                          {r.notes && (
                            <p className="text-[11px] text-slate-500 mt-1 italic">{r.notes}</p>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1 shrink-0">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(r)} className="text-amber-300 hover:bg-amber-500/10 h-8 w-8" data-testid={`rc-edit-${r.id}`}><Edit3 className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(r)} className="text-red-400 hover:bg-red-500/10 h-8 w-8" data-testid={`rc-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                        {(r.price > 0 || r.unit) && (
                          <>
                            <span className="text-2xl font-extrabold shine-gold-text">₹{Number(r.price || 0).toLocaleString('en-IN')}</span>
                            {r.unit && <span className="text-xs text-slate-400">{r.unit}</span>}
                          </>
                        )}
                      </div>

                      {/* Attachments */}
                      <div className="mt-3 pt-3 border-t border-amber-500/10">
                        {(r.attachments || []).length === 0 ? (
                          <div className="text-[11px] text-slate-500 italic">No files attached yet.</div>
                        ) : (
                          <div className="space-y-1.5" data-testid={`rc-attachments-${r.id}`}>
                            {r.attachments.map((att) => {
                              const Icon = (att.mime || '').startsWith('image/') ? ImageIcon : FileText;
                              return (
                                <div key={att.id} className="flex items-center gap-2 bg-[#0f2240] border border-amber-500/20 rounded-md px-2 py-1.5">
                                  <Icon className="h-4 w-4 text-amber-300 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs text-white truncate" title={att.filename}>{att.filename}</div>
                                    <div className="text-[10px] text-slate-400">{formatBytes(att.size)}</div>
                                  </div>
                                  <Button size="icon" variant="ghost" onClick={() => downloadAttachment(r, att)} className="h-6 w-6 text-emerald-300 hover:bg-emerald-500/10" title="Download" data-testid={`rc-att-dl-${att.id}`}>
                                    <Download className="h-3.5 w-3.5" />
                                  </Button>
                                  {canEdit && (
                                    <Button size="icon" variant="ghost" onClick={() => removeAttachment(r, att)} className="h-6 w-6 text-red-400 hover:bg-red-500/10" title="Remove" data-testid={`rc-att-rm-${att.id}`}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {canEdit && (
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openFilePicker(r.id)}
                              disabled={uploading}
                              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 w-full"
                              data-testid={`rc-att-add-${r.id}`}
                            >
                              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5 mr-1.5" />}
                              {uploading ? 'Uploading…' : 'Attach PDF / image'}
                            </Button>
                            <input
                              ref={(el) => { fileInputsRef.current[r.id] = el; }}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                              multiple
                              onChange={(e) => handleAttachmentPick(r.id, e)}
                              className="hidden"
                              data-testid={`rc-att-input-${r.id}`}
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==================== CREATE / EDIT DIALOG ==================== */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Edit rate card' : 'New rate card'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category<Required /></Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20"
                placeholder="e.g. YouTube Ads"
                data-testid="rc-form-category"
                aria-required="true"
              />
            </div>
            <div>
              <Label>Service<Required /></Label>
              <Input
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20"
                placeholder="e.g. 1-min Skippable Ad"
                data-testid="rc-form-service"
                aria-required="true"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0f2240] border-amber-500/20 min-h-[90px]"
                placeholder="What&apos;s included, deliverables, timelines…"
                data-testid="rc-form-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="per month / per reel" />
              </div>
              <div>
                <Label>Price (₹)</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0f2240] border-amber-500/20" />
              </div>
            </div>
            <div>
              <Label>Internal notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="Only visible to internal team" />
            </div>
            {!canSubmit && (
              <div className="text-[11px] text-amber-300">Category and Service are required.</div>
            )}
            {!editing && (
              <div className="text-[11px] text-slate-400">You can attach PDFs / images after saving.</div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!canSubmit || saving} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="rc-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
